'use client';

import { Check, Shield, ArrowLeft, Building2, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import Link from 'next/link';

export default function PricingPage() {
    const { lang } = useLanguage();
    const [billingCycle, setBillingCycle] = useState<'A_LA_CARTE' | 'ABONNEMENT'>('A_LA_CARTE');
    const [loading, setLoading] = useState<string | null>(null);

    const handleCheckout = async (planId: string) => {
        const token = localStorage.getItem('vision_token');
        if (!token) {
            alert(lang === 'FR' ? "Veuillez vous connecter ou créer un compte depuis l'accueil pour effectuer une souscription." : "Please sign in or create an account to subscribe.");
            window.location.href = '/';
            return;
        }

        try {
            setLoading(planId);
            const { data } = await axios.post('/stripe/create-checkout-session', { planId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.href = data.url;
        } catch (error: any) {
            console.error(error);
            alert(error.response?.data?.message || (lang === 'FR' ? "Erreur lors de la redirection vers le paiement sécurisé." : "Payment redirect error."));
            setLoading(null);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-[#090A0F] py-16 px-4">
            <div className="max-w-6xl mx-auto space-y-12">
                
                {/* Top Nav */}
                <div className="flex items-center justify-between">
                    <Link href="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-950 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        OOTENTIK VISION
                    </Link>

                    <span className="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">
                        GRILLE TARIFAIRE OFFICIELLE • B2B & PARTICULIERS
                    </span>
                </div>

                {/* Hero Header */}
                <div className="text-center space-y-4 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 text-zinc-800 text-[11px] font-mono font-bold uppercase tracking-wider">
                        TRANSPARENCE INDUSTRIELLE • AUCUN FRAIS CACHÉ
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-950">
                        {lang === 'FR' ? "Tarification d'Expertise Forensique" : "Forensic Inspection Pricing"}
                    </h1>
                    <p className="text-sm sm:text-base text-zinc-600">
                        {lang === 'FR' 
                            ? "Investissez dans la sécurité de vos transactions de luxe. Rapport d'inspection officiel et certificat numérique inclus avec chaque analyse." 
                            : "Secure your high-value luxury acquisitions. Official inspection report and digital seal included with every audit."}
                    </p>

                    {/* Toggle Selector */}
                    <div className="pt-4 flex justify-center">
                        <div className="inline-flex p-1 bg-zinc-200/80 rounded-2xl">
                            <button
                                type="button"
                                onClick={() => setBillingCycle('A_LA_CARTE')}
                                className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${billingCycle === 'A_LA_CARTE' ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
                            >
                                {lang === 'FR' ? "À l'Acte (Crédits Permanents)" : "Pay-As-You-Go"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setBillingCycle('ABONNEMENT')}
                                className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${billingCycle === 'ABONNEMENT' ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
                            >
                                {lang === 'FR' ? "Abonnements Professionnels" : "Monthly Subscriptions"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {billingCycle === 'A_LA_CARTE' ? (
                        <>
                            {/* Card 1 */}
                            <div className="bg-white rounded-3xl p-8 border border-zinc-200 shadow-sm flex flex-col justify-between">
                                <div className="space-y-4">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                                        CONTRÔLE PONCTUEL
                                    </span>
                                    <h3 className="text-2xl font-black text-zinc-950">
                                        1 Expertise Forensique
                                    </h3>
                                    <p className="text-xs text-zinc-500">
                                        Idéal pour vérifier une montre ou un sac avant un achat entre particuliers.
                                    </p>
                                    <div className="pt-4 flex items-baseline gap-1">
                                        <span className="text-4xl font-black text-zinc-950">7 €</span>
                                        <span className="text-xs text-zinc-400 font-medium">HT / unité</span>
                                    </div>
                                    <ul className="pt-4 space-y-2.5 text-xs text-zinc-700">
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Analyse 5 à 10 photographies macro</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Certificat PDF officiel téléchargeable</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Lien public de vérification permanent</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Crédit valable à vie sans expiration</li>
                                    </ul>
                                </div>
                                <button
                                    onClick={() => handleCheckout('act_1')}
                                    disabled={loading === 'act_1'}
                                    className="mt-8 w-full py-3.5 rounded-xl bg-zinc-900 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                                >
                                    {loading === 'act_1' ? "Chargement..." : "Acheter 1 crédit (7 €)"}
                                </button>
                            </div>

                            {/* Card 2 */}
                            <div className="bg-white rounded-3xl p-8 border-2 border-zinc-950 shadow-md flex flex-col justify-between relative">
                                <div className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-zinc-950 text-white font-mono text-[9px] uppercase tracking-wider font-bold">
                                    RECOMMANDÉ PRO
                                </div>
                                <div className="space-y-4">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                                        PACK REVENDEDUR INDÉPENDANT
                                    </span>
                                    <h3 className="text-2xl font-black text-zinc-950">
                                        Pack 10 Expertises
                                    </h3>
                                    <p className="text-xs text-zinc-500">
                                        Conçu pour les marchands et boutiques de seconde main traitant des volumes réguliers.
                                    </p>
                                    <div className="pt-4 flex items-baseline gap-1">
                                        <span className="text-4xl font-black text-zinc-950">50 €</span>
                                        <span className="text-xs text-zinc-400 font-medium">HT (soit 5 € / analyse)</span>
                                    </div>
                                    <ul className="pt-4 space-y-2.5 text-xs text-zinc-700">
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Économie de 20 € par rapport au tarif unitaire</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Priorité de calcul sur le moteur IA</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Export comptable & factures automatiques</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Crédits valables à vie sans engagement</li>
                                    </ul>
                                </div>
                                <button
                                    onClick={() => handleCheckout('act_10')}
                                    disabled={loading === 'act_10'}
                                    className="mt-8 w-full py-3.5 rounded-xl bg-zinc-950 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                                >
                                    {loading === 'act_10' ? "Chargement..." : "Acheter le Pack 10 (50 €)"}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Pro Sub */}
                            <div className="bg-white rounded-3xl p-8 border border-zinc-200 shadow-sm flex flex-col justify-between">
                                <div className="space-y-4">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                                        OFFRE COMMERCE CERTIFIÉ
                                    </span>
                                    <h3 className="text-2xl font-black text-zinc-950">
                                        Abonnement PRO
                                    </h3>
                                    <p className="text-xs text-zinc-500">
                                        50 analyses mensuelles pour marchands d'horlogerie et maroquinerie de luxe.
                                    </p>
                                    <div className="pt-4 flex items-baseline gap-1">
                                        <span className="text-4xl font-black text-zinc-950">99 €</span>
                                        <span className="text-xs text-zinc-400 font-medium">HT / mois</span>
                                    </div>
                                    <ul className="pt-4 space-y-2.5 text-xs text-zinc-700">
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> 50 analyses forensiques incluses / mois</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> 10 requêtes The Watch Register® incluses</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Certificats personnalisés au nom de votre boutique</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Sans engagement, résiliable à tout moment</li>
                                    </ul>
                                </div>
                                <button
                                    onClick={() => handleCheckout('sub_pro')}
                                    disabled={loading === 'sub_pro'}
                                    className="mt-8 w-full py-3.5 rounded-xl bg-zinc-900 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                                >
                                    {loading === 'sub_pro' ? "Chargement..." : "Souscrire PRO (99 €/mois)"}
                                </button>
                            </div>

                            {/* Expert Sub */}
                            <div className="bg-white rounded-3xl p-8 border-2 border-zinc-950 shadow-md flex flex-col justify-between relative">
                                <div className="space-y-4">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                                        MAISONS DE VENTES & GRANDS VOLUMES
                                    </span>
                                    <h3 className="text-2xl font-black text-zinc-950">
                                        Abonnement EXPERT
                                    </h3>
                                    <p className="text-xs text-zinc-500">
                                        100 analyses mensuelles + support prioritaire et quotas étendus.
                                    </p>
                                    <div className="pt-4 flex items-baseline gap-1">
                                        <span className="text-4xl font-black text-zinc-950">189 €</span>
                                        <span className="text-xs text-zinc-400 font-medium">HT / mois</span>
                                    </div>
                                    <ul className="pt-4 space-y-2.5 text-xs text-zinc-700">
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> 100 analyses forensiques incluses / mois</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> 50 requêtes The Watch Register® incluses</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Accès API REST pour intégration catalogue</li>
                                        <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Support technique dédié et SLA prioritaire</li>
                                    </ul>
                                </div>
                                <button
                                    onClick={() => handleCheckout('sub_expert')}
                                    disabled={loading === 'sub_expert'}
                                    className="mt-8 w-full py-3.5 rounded-xl bg-zinc-950 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                                >
                                    {loading === 'sub_expert' ? "Chargement..." : "Souscrire EXPERT (189 €/mois)"}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* Enterprise Contact Box */}
                <div className="max-w-4xl mx-auto p-8 rounded-3xl bg-zinc-950 text-white flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 font-bold block mb-1">
                            COMMISSAIRES-PRISEURS & PLATEFORMES E-COMMERCE
                        </span>
                        <h4 className="text-lg font-bold">Besoin de volumes sur-mesure ou d'un flux API dédié ?</h4>
                        <p className="text-xs text-zinc-400 mt-1 max-w-xl">
                            Nos équipes déploient des pipelines personnalisés pour l'audit de catalogues entiers et l'intégration directe dans vos logiciels de gestion.
                        </p>
                    </div>
                    <a
                        href="mailto:contact@ootentik.online"
                        className="px-6 py-3 rounded-xl bg-white text-zinc-950 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 transition-colors whitespace-nowrap"
                    >
                        Contacter la Direction
                    </a>
                </div>
            </div>
        </div>
    );
}
